import type { EChartsOption } from 'echarts'
import { axisTextStyle, formatCompactNumber, formatShare, isFiniteNumber, tooltipBase, tooltipHtml } from './format'
import { seriesColor, type ChartTheme } from './palette'

export interface ParetoItem {
  key: string
  label: string
  value: number | null
}

export interface ParetoInput {
  items: ParetoItem[]
  /** Границы классов по накопленной доле, по умолчанию A ≤ 80 %, B ≤ 95 %, дальше C. */
  thresholds?: [number, number]
  valueFormat?: (value: number) => string
  /** Подписи классов. */
  classLabels?: [string, string, string]
}

export interface ParetoRow {
  item: ParetoItem
  value: number
  share: number
  cumulative: number
  /** Класс 0 = A, 1 = B, 2 = C. */
  klass: 0 | 1 | 2
}

/**
 * Строки Парето: по убыванию значения, доля каждой и накопленная доля (обе 0..1), класс по накопленной доле
 * *до* строки: строка, начавшаяся раньше границы A, ещё входит в A — так первая же крупная позиция не
 * выпадает из A, даже если одна перекрывает 80 %. Пустые и неположительные значения не участвуют.
 */
export function paretoRows(items: ParetoItem[], thresholds: [number, number] = [0.8, 0.95]): ParetoRow[] {
  const valid = items.filter((item) => isFiniteNumber(item.value) && (item.value as number) > 0)
  const sorted = [...valid].sort((a, b) => (b.value as number) - (a.value as number))
  const total = sorted.reduce((sum, item) => sum + (item.value as number), 0)
  if (total <= 0) return []
  let running = 0
  return sorted.map((item) => {
    const before = running
    const value = item.value as number
    running += value
    const klass: 0 | 1 | 2 = before < thresholds[0] * total ? 0 : before < thresholds[1] * total ? 1 : 2
    return { item, value, share: value / total, cumulative: running / total, klass }
  })
}

/** Номер последней строки каждого класса (для вертикальных границ); −1 — класса нет. */
export function classBoundaries(rows: ParetoRow[]): [number, number] {
  let lastA = -1
  let lastB = -1
  rows.forEach((row, index) => {
    if (row.klass === 0) lastA = index
    if (row.klass === 1) lastB = index
  })
  return [lastA, lastB]
}

/**
 * Парето: столбцы долей и накопленная линия — обе в % на одной оси 0–100 %. Горизонтальные отметки — пороги
 * классов, вертикальные — где класс кончается. Две серии — две сущности, поэтому легенда есть.
 */
export function buildParetoOption(input: ParetoInput, theme: ChartTheme): EChartsOption {
  const thresholds = input.thresholds ?? [0.8, 0.95]
  const rows = paretoRows(input.items, thresholds)
  const [lastA, lastB] = classBoundaries(rows)
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const labels = input.classLabels ?? ['A', 'B', 'C']
  const barColor = seriesColor(theme, 0)
  const lineColor = seriesColor(theme, 1)
  const many = rows.length > 40

  const verticals = [
    lastA >= 0 && lastA < rows.length - 1 ? { xAxis: lastA + 0.5, label: `${labels[0]} | ${labels[1]}` } : null,
    lastB >= 0 && lastB < rows.length - 1 ? { xAxis: lastB + 0.5, label: `${labels[1]} | ${labels[2]}` } : null,
  ].filter(Boolean) as Array<{ xAxis: number; label: string }>

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    legend: {
      top: 0,
      left: 0,
      itemWidth: 14,
      itemHeight: 8,
      textStyle: { color: theme.textSecondary, fontFamily: theme.fontFamily, fontSize: 12 },
      data: [
        { name: 'Доля', icon: 'roundRect' },
        { name: 'Накопленная доля' },
      ],
    },
    grid: { top: 36, left: 8, right: 16, bottom: 8, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: theme.textMuted, width: 1 } },
      formatter: (raw: any) => {
        const params = Array.isArray(raw) ? raw : [raw]
        const row = rows[params[0]?.dataIndex ?? 0]
        if (!row) return ''
        return tooltipHtml(
          theme,
          row.item.label,
          [
            { color: barColor, label: 'доля', value: formatShare(row.share), note: valueFormat(row.value) },
            { color: lineColor, label: 'накопленная доля', value: formatShare(row.cumulative) },
          ],
          [`Класс ${labels[row.klass]}`],
        )
      },
    },
    xAxis: {
      type: 'category',
      data: rows.map((row) => row.item.label),
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: many
        ? { show: false }
        : { ...axisTextStyle(theme), interval: 0, rotate: rows.length > 12 ? 40 : 0, width: 90, overflow: 'truncate' },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      interval: 0.2,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => `${Math.round(value * 100)}%` },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
    },
    series: [
      {
        name: 'Доля',
        type: 'bar',
        barMaxWidth: 24,
        barCategoryGap: many ? '10%' : '30%',
        cursor: 'pointer',
        itemStyle: { color: barColor, borderRadius: [4, 4, 0, 0] },
        data: rows.map((row) => row.share),
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: theme.textMuted, width: 1, type: 'solid' },
          label: { color: theme.textSecondary, fontSize: 11, fontFamily: theme.fontFamily },
          data: [
            { yAxis: thresholds[0], label: { formatter: `${Math.round(thresholds[0] * 100)}%`, position: 'insideEndTop' } },
            { yAxis: thresholds[1], label: { formatter: `${Math.round(thresholds[1] * 100)}%`, position: 'insideEndTop' } },
            ...verticals.map((line) => ({ xAxis: line.xAxis, label: { formatter: line.label, position: 'insideEndTop' } })),
          ],
        },
      },
      {
        name: 'Накопленная доля',
        type: 'line',
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: !many,
        lineStyle: { width: 2, color: lineColor },
        itemStyle: { color: lineColor, borderColor: theme.surface, borderWidth: 2 },
        data: rows.map((row) => row.cumulative),
      },
    ],
  } as EChartsOption
}
